/**
 * DataBinding: where a table, repeater, chart or value in a document gets its data (plan §7.3).
 * Evaluated by insight-engine; shared by pages, slides, document templates and dashboards.
 *
 * Owner: doc-engine (wave 1), dashboards-core (wave 2).
 */
import { z } from 'zod';
import { FilterNodeSchema, SortSpec, type FilterNode } from '../filter';
import { ApiKey } from '../ids';
import { JsonValue, type Json } from '../json';
import { LocalizedText } from '../localized-text';
import { FieldTypeKey } from '../metamodel';
import { ExprSource, ExprTypeRef, type ExprSite } from './expr-site';

export * from './expr-site';

/** `@link` steps from a relation into its link fields (`['line_items', '@link', 'quantity']`). */
export const LINK_STEP = '@link';
const SEGMENT_RE = /^[a-z][a-z0-9_]*$/;

/** A field path as segments. `['company', 'industry']` is the relation path `company.industry`. */
export const FieldPath = z
  .array(z.string())
  .min(1)
  .max(8)
  .superRefine((path, ctx) => {
    path.forEach((segment, i) => {
      if (segment === LINK_STEP) {
        if (i === 0 || i === path.length - 1 || path[i - 1] === LINK_STEP)
          ctx.addIssue({
            code: 'custom',
            path: [i],
            message: '@link must follow a relation and precede a link field',
          });
      } else if (!SEGMENT_RE.test(segment) || segment.length > 63)
        ctx.addIssue({ code: 'custom', path: [i], message: 'Expected a field key' });
    });
  });
export type FieldPath = string[];

export function fieldPathFromDotted(path: string): FieldPath {
  return path.split('.').filter(Boolean);
}
/** Dotted form used by filters and sorts (`company.industry`); `@link` stays a segment. */
export function fieldPathToDotted(path: FieldPath): string {
  return path.join('.');
}

export const SortList = z.array(SortSpec).max(5);
export const BindingLimit = z.number().int().min(1).max(1000);

export const StaticColumn = z.object({ key: ApiKey, label: LocalizedText, type: FieldTypeKey }).strict();
export type StaticColumn = z.infer<typeof StaticColumn>;

export const StaticBinding = z
  .object({
    kind: z.literal('static'),
    columns: z.array(StaticColumn).min(1).max(50),
    rows: z.array(z.array(JsonValue)).max(1000),
  })
  .strict()
  .superRefine((b, ctx) => {
    const keys = new Set<string>();
    b.columns.forEach((c, i) => {
      if (keys.has(c.key))
        ctx.addIssue({ code: 'custom', path: ['columns', i, 'key'], message: 'Column keys must be unique' });
      keys.add(c.key);
    });
    b.rows.forEach((row, i) => {
      if (row.length !== b.columns.length)
        ctx.addIssue({ code: 'custom', path: ['rows', i], message: 'Every row needs one value per column' });
    });
  });
export type StaticBinding = {
  kind: 'static';
  columns: StaticColumn[];
  rows: Json[][];
};

const QuerySource = z.union([
  z.object({ object: ApiKey }).strict(),
  z.object({ view: z.string().min(1).max(80) }).strict(),
]);

export const QueryBinding = z
  .object({
    kind: z.literal('query'),
    source: QuerySource,
    filter: FilterNodeSchema.optional(),
    sort: SortList.optional(),
    limit: BindingLimit.optional(),
    fields: z.array(FieldPath).max(50).optional(),
  })
  .strict();

export const RelationBinding = z
  .object({
    kind: z.literal('relation'),
    path: FieldPath,
    filter: FilterNodeSchema.optional(),
    sort: SortList.optional(),
    limit: BindingLimit.optional(),
    fields: z.array(FieldPath).max(50).optional(),
  })
  .strict();

export const ExpressionBinding = z
  .object({ kind: z.literal('expression'), expr: ExprSource, expected: ExprTypeRef.optional() })
  .strict();

export const DataBinding = z.union([StaticBinding, QueryBinding, RelationBinding, ExpressionBinding]);
export type DataBinding =
  | StaticBinding
  | {
      kind: 'query';
      source: { object: string } | { view: string };
      filter?: FilterNode;
      sort?: SortSpec[];
      limit?: number;
      fields?: FieldPath[];
    }
  | {
      kind: 'relation';
      path: FieldPath;
      filter?: FilterNode;
      sort?: SortSpec[];
      limit?: number;
      fields?: FieldPath[];
    }
  | { kind: 'expression'; expr: string; expected?: ExprTypeRef };

/** Expression sites inside a binding (path relative to the binding). */
export function bindingExprSites(binding: DataBinding, context: ExprSite['context'] = 'record'): ExprSite[] {
  if (binding.kind !== 'expression') return [];
  return [
    {
      path: ['expr'],
      source: binding.expr,
      role: 'expression',
      context,
      ...(binding.expected ? { expected: binding.expected } : {}),
    },
  ];
}

/** Prefix the paths of expression sites (used when a site is nested in a larger artefact). */
export function prefixSites(prefix: (string | number)[], sites: ExprSite[]): ExprSite[] {
  return sites.map((s) => ({ ...s, path: [...prefix, ...s.path] }));
}
